import { CouponArt } from "@/components/marketing/coupon/CouponArt"
import { Button, Card, Heading, Text } from "@/components/ui"
import { SPRING, TRANSITION } from "@/lib/motion"
import * as Dialog from "@radix-ui/react-dialog"
import { motion, useReducedMotion } from "motion/react"
import { useState, type ReactNode } from "react"
import { PrizeGameButton, PrizeGameLink } from "./PrizeGameButton"
import { PrizeGameSheet } from "./PrizeGameSheet"
import { WheelConfetti } from "./PrizeWheelConfetti"

/** What the reveal sheet shows: a coupon to celebrate, or one that does not work now and why. */
export type CouponRevealContent =
  | { kind: "won"; code: string; eyebrow: string; headline: string; detail: string | null; conditions: string[]; replaced: string[] }
  | { kind: "not-usable"; code: string; title: string; message: string }

type CouponRevealSheetProps = {
  open: boolean
  content: CouponRevealContent
  onUse: () => void
  onBrowse: () => void
  onUndoReplace: () => void
  itemAction?: ReactNode
}

function CodeSlip({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)

  async function copy() {
    try {
      await navigator.clipboard.writeText(code)
      setCopied(true)
    } catch {
      setCopied(false)
    }
  }

  return (
    <Card variant="stub" tone="game" padding="compact" className="flex w-full items-center justify-between gap-3">
      <Text as="strong" size="lg" weight="bold" tracking="wide" leading="none" tone="default" className="select-all break-all font-mono">
        {code}
      </Text>
      <Button variant="text" tone="game" size="auto" onClick={() => void copy()} aria-label={`Copiar o código ${code}`}>
        <Text as="span" size="sm" weight="semibold">
          {copied ? "Copiado" : "Copiar"}
        </Text>
      </Button>
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? "Código copiado" : ""}
      </span>
    </Card>
  )
}

function RevealArt({ celebrate }: { celebrate: boolean }) {
  const reduceMotion = useReducedMotion()
  return (
    <div className="relative grid h-40 w-full place-items-center">
      {celebrate && !reduceMotion ? <WheelConfetti /> : null}
      <motion.div
        initial={reduceMotion ? { opacity: 0 } : { opacity: 0, scale: 0.6, y: 24, rotate: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
        transition={reduceMotion ? TRANSITION.fade : { ...SPRING.pop, delay: 0.12 }}
      >
        <CouponArt size={148} muted={!celebrate} />
      </motion.div>
    </div>
  )
}

/**
 * The coupon a visitor brought in the link, revealed on the game sheet: the
 * 3D coupon springs in over a one-shot confetti burst, the benefit reads big,
 * the code sits in a dashed slip and the fine print comes from the public
 * record. A coupon that does not work now gets the same sheet, calm, with why.
 */
export function CouponRevealSheet({ open, content, onUse, onBrowse, onUndoReplace, itemAction }: CouponRevealSheetProps) {
  const won = content.kind === "won"
  return (
    <PrizeGameSheet open={open} onOpenChange={(next) => (next ? undefined : onBrowse())}>
      <div className="flex flex-col items-center gap-4 pb-2 pt-1 text-center">
        <RevealArt celebrate={won} />

        {won ? (
          <div className="flex flex-col items-center gap-1">
            <Text size="game-eyebrow" weight="bold" uppercase tracking="ticket" tone="game-accent">
              {content.eyebrow}
            </Text>
            <Dialog.Title asChild>
              <Heading level={2} size="page" leading="tight" family="game-display">
                Você ganhou um cupom!
              </Heading>
            </Dialog.Title>
            <Text as="p" size="4xl" weight="bold" leading="none" family="game-display" tone="default" className="mt-2">
              {content.headline}
            </Text>
            {content.detail ? (
              <Text size="base" tone="muted">
                {content.detail}
              </Text>
            ) : null}
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2">
            <Text size="game-eyebrow" weight="bold" uppercase tracking="ticket" tone="muted">
              Cupom recebido
            </Text>
            <Dialog.Title asChild>
              <Heading level={2} size="page" leading="tight" family="game-display">
                {content.title}
              </Heading>
            </Dialog.Title>
            <Text size="sm" leading="relaxed" tone="muted" className="max-w-sm">
              {content.message}
            </Text>
          </div>
        )}

        <CodeSlip code={content.code} />

        {won && content.replaced.length > 0 ? (
          <Text size="sm" tone="muted">
            Ele entrou no lugar do cupom {content.replaced.join(", ")}, que não combina com este.{" "}
            <Button variant="link" tone="game" size="auto" weight="semibold" onClick={onUndoReplace}>
              <Text as="span" size="sm">
                Desfazer
              </Text>
            </Button>
          </Text>
        ) : null}

        {won && content.conditions.length > 0 ? (
          <ul aria-label="Condições do cupom" className="flex w-full flex-col gap-1">
            {content.conditions.map((line) => (
              <Text as="li" key={line} size="sm" tone="muted">
                {line}
              </Text>
            ))}
          </ul>
        ) : null}

        <div className="flex w-full flex-col gap-1 pt-1">
          {won ? (
            <>
              {itemAction}
              <PrizeGameButton onClick={onUse}>Usar agora</PrizeGameButton>
              <PrizeGameLink onClick={onBrowse}>Ver cardápio</PrizeGameLink>
            </>
          ) : (
            <PrizeGameButton onClick={onBrowse}>Ver cardápio</PrizeGameButton>
          )}
        </div>
      </div>
    </PrizeGameSheet>
  )
}
